// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { TaskForm } from '~/components/module/task';
import {
  BaseAvatarGroup,
  BaseMoreAction,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
} from '~/components/common';
// Others
import { ITask } from '~/utils/interface/task';
import { deleteTask } from '~/thunks/task/taskThunk';
import { taskActions } from '~/thunks/task/taskSlice';
import { getDetailJob } from '~/thunks/job/jobThunk';
import { IParamQueryJobDetail } from '~/utils/interface/jobs';
import { formatDate, getFullName } from '~/utils/helper/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IBaseMoreAction, IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { BusinessTypeEnum, DateFormatEnum, TypeJobDetailEnum } from '~/utils/enum/common';
import { IBaseResponse, IListDataResponse, IPaginationResponse } from '~/utils/interface/base';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE, EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './TaskJobTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (record: ITask) => void,
  onEdit: (record: ITask) => void,
  onDelete: (record: ITask) => void
): IHeaderCellType<ITask>[] => {
  return [
    {
      id: 'title',
      label: t('task_table_task_title'),
      render: (record: ITask) => (
        <Stack direction={'column'} maxWidth={350}>
          <Typography className={cx('textTitleTable')}>{record?.title}</Typography>
          <Typography className={cx('textNoteTable')}>{record?.description}</Typography>
        </Stack>
      ),
    },
    {
      id: 'assignees',
      label: t('task_table_assignee_title'),
      render: (record: ITask) => {
        const assignees = record?.assignees?.map((user) => {
          return {
            avatarImage: user.avatarImage,
            fullName: getFullName({
              firstName: user.firstName,
              lastName: user.lastName,
            }),
            id: user.id,
          };
        });

        return record?.assignees?.length ? <BaseAvatarGroup avatars={assignees} /> : EMPTY_VALUE;
      },
      minWidth: 140,
    },
    {
      id: 'due date',
      label: t('task_table_due_date_title'),
      render: (record: ITask) => {
        return (
          <Typography className={cx('textTable')}>
            {formatDate(record?.dueDate, DateFormatEnum.M_D_YYYY) || EMPTY_VALUE}
          </Typography>
        );
      },
      minWidth: 140,
    },
    {
      id: 'priority',
      label: t('task_table_priority_title'),
      render: (record: ITask) =>
        record?.priority ? <BaseStatus label={record?.priority || EMPTY_STRING} /> : EMPTY_VALUE,
    },
    {
      id: 'status',
      label: t('task_table_status_title'),
      render: (record: ITask) => (record?.status ? <BaseStatus label={record?.status || EMPTY_STRING} /> : EMPTY_VALUE),
    },
    {
      id: 'action',
      label: t('task_table_action_title'),
      minWidth: 100,
      align: 'center',
      render: (record: ITask) => {
        const actions: IBaseMoreAction[] = [
          {
            label: t('table_action_view', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.tableIconActionView}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: () => onView(record),
          },
          {
            label: t('table_action_edit', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.tableIconActionEdit}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: () => onEdit(record),
          },
          {
            label: t('table_action_delete', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.tableIconActionDelete}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            hasOtherColor: true,
            onClick: () => onDelete(record),
          },
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const TaskJobTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshTaskList } = useAppSelector((state) => state.task);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowFormTask, setIsShowFormTask] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);

  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [taskList, setTaskList] = useState<ITask[]>([]);
  const [taskIdSelected, setTaskIdSelected] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IParamQueryJobDetail = {
      jobId: jobId || EMPTY_STRING,
      type: TypeJobDetailEnum.TASKS,
      query: paramObject,
    };

    handleGetListTaskJob(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshTaskList) return;

    const params: IParamQueryJobDetail = {
      jobId: jobId || EMPTY_STRING,
      type: TypeJobDetailEnum.TASKS,
      query: paramObject,
    };

    handleGetListTaskJob(params);

    dispatch(taskActions.setRefreshTaskList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTaskList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListTaskJob = (params: IParamQueryJobDetail) => {
    setIsLoading(true);

    dispatch(getDetailJob(params))
      .unwrap()
      .then((res) => {
        const { data } = res as IBaseResponse<IListDataResponse<ITask[]>>;

        if (!data) return;

        const { responses, pagination } = data;

        if (!responses) return;

        setTaskList(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleView = (item: ITask) => {
    if (!item) return;

    setTaskIdSelected(item?.id);
    handleShowFormTask();
  };

  const handleEdit = (item: ITask) => {
    if (!item) return;

    setTaskIdSelected(item?.id);
    handleShowFormTask();
  };

  const handleDelete = (item: ITask) => {
    if (!item) return;

    setTaskIdSelected(item?.id);
    setIsShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteTask(taskIdSelected))
      .unwrap()
      .then(() => {
        handleCloseConfirmDelete();
        dispatch(taskActions.setRefreshTaskList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowFormTask = () => {
    setIsShowFormTask(true);
  };

  const handleCloseFormTask = () => {
    setIsShowFormTask(false);
    setTaskIdSelected(EMPTY_STRING);
  };

  const handleCloseConfirmDelete = () => {
    setTaskIdSelected(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };
  //#endregion Handle Function

  return (
    <Stack id='taskJobDetailTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <Typography className={cx('title')}>{t('task_title')}</Typography>

        <Button
          type='button'
          variant='contained'
          className={cx('buttonAction')}
          onClick={handleShowFormTask}
          startIcon={<img src={icons.commonIconPlus} alt={t('common_alt_img', { ns: 'common/base' })} />}
        >
          {t('task_add_task_button')}
        </Button>
      </Stack>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={taskList || []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t, handleView, handleEdit, handleDelete)}
          onPageChange={handlePageChange}
          onRowClick={(_id, record) => handleView(record)}
        />
      </Box>

      {isShowFormTask && (
        <TaskForm taskId={taskIdSelected} onClose={handleCloseFormTask} businessType={BusinessTypeEnum.JOB} />
      )}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default TaskJobTab;
